import React, { useEffect, useState } from "react";
import { PrimaryButton } from "../UI";
import { useUserContext } from "../../contexts/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../UI/Alert/SuccessAlert";
import Label from "../UI/Label";
import Loader from "../UI/Loader/Loader";
import { Field, Form, Formik } from "formik";
import { AdobeTargetActivitiesRevisionsSchema } from "../../schemas";
import Dropdown from "../UI/Dropdown/Dropdown";
import { getSavedTasks, saveTask, updateTask } from "../../utils/APIs";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import Tooltip from "../UI/Tooltip";
import InlineTooltip from "../UI/Tooltip/InlineTooltip";

const AdobeTargetActivitiesRevisions = () => {
    const { userData } = useUserContext();
    const { content, enums } = useContentfulContext();

    const staticContant = content.ADOBE_TARGET_ACTIVITIES_REVISIONS || {
        title: "Fetch Manage Activity Revisions",
        description: "Fetch and save Manage Activity Revisions",
        formLabels: {
            adobe_io_integration_id: "Adobe IO Integration",
            activity_state: "State",
            modified_year: "Modified Year"
        },
        help_text: {
            adobe_io_integration_id: "Select the Adobe IO integration to use for this task",
            activity_state: "Select the activity state to filter by",
            modified_year: "Select the modified year to filter by"
        },
        activity_state_options: [
            { value: "all", name: "All" },
            { value: "approved", name: "Approved" },
            { value: "saved", name: "Saved" },
            { value: "deleted", name: "Deleted" }
        ],
        modified_year_options: [
            { value: "all", name: "All" },
            { value: "2023", name: "2023" },
            { value: "2024", name: "2024" },
            { value: "2025", name: "2025" },
            { value: "2026", name: "2026" },
            { value: "2027", name: "2027" }
        ],
        VALIDATE_NAME_CONTENT: {
            title: "Validate Name",
            errors: {
                FE133: "Your name can only contain letters, numbers, dots (.), dashes (-), and underscores (_).",
                FE134: "Enter your name",
                FE135: "Select your time interval",
                FE136: "Select your status",
                FE137: "Enter valid email",
                FE138: "Enter at least one recipient email",
                FE139: "Enter your regular expression",
                FE140: "Enter valid regular expression",
                FE141: "Enter filter value"
            },
            success: "Validate Name created successfully.",
            timeout: 5000,
            formLabels: {
                email: "Recipient Email",
                regex: "Regular Expression",
                status: "Status",
                time_interval: "Time Interval",
                saved_task_name: "Task Name",
                recipient_emails: "Recipient Emails",
                adobe_io_integration: "Adobe IO Integration"
            },
            submit_cta: "Submit",
            description: "It will validate all users' email-addresses using Name server."
        },
        VALIDATE_ACTIVITY_NAME_CONTENT: {
            title: "Validate Activity Name",
            note: "Note: Press Enter to add the email.",
            errors: {
                FE133: "Your name can only contain letters, numbers, dots (.), dashes (-), and underscores (_).",
                FE134: "Enter your name",
                FE135: "Select your time interval",
                FE136: "Select your status",
                FE137: "Enter valid email",
                FE138: "Enter at least one recipient email",
                FE139: "Enter your regular expression",
                FE140: "Enter valid regular expression"
            },
            success: "Validate Activity Name task created successfully.",
            timeout: 5000,
            formLabels: {
                email: "Recipient Email",
                regex: "Regular Expression",
                status: "Status",
                time_interval: "Time Interval",
                saved_task_name: "Task Name",
                recipient_emails: "Recipient Emails",
                adobe_io_integration: "Adobe IO Integration"
            },
            help_text: {
                saved_task_name: "Please enter task name *",
                time_interval: "Please select time interval *",
                status: "Please select status *",
                regex: "Please enter regular expression *",
                filter_type: "Please select filter type *",
                filter_value: "Please enter filter value *",
                recipient_emails: "Please enter at least one recipient email *"
            },
            submit_cta: "Submit",
            description: "It will validate all activities name."
        }
    };

    const { pathname } = useLocation();
    const { saved_task_id } = useParams()
    const navigate = useNavigate()

    const [showSuccess, setShowSuccess] = useState("");
    const [showError, setShowError] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState();
    const [activityState, setActivityState] = useState("all");
    const [modifiedYear, setModifiedYear] = useState("all");

    useEffect(() => {
        if(saved_task_id) getSavedTasksDataById()
    }, [])
    
    const getSavedTasksDataById = async () => {
        try {
            setLoading(true);
            const result = await getSavedTasks(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                saved_task_id
            );
            setData(result.data[0]);
            setActivityState(result.data[0]?.activity_state || "all");
            setModifiedYear(result.data[0]?.modified_year || "all");
        } catch (error) {
            console.log("error getSavedTasksDataById: ", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            setShowError("");
            let result;
            if(saved_task_id) {
                const data = {
                    time_interval: values?.time_interval,
                    status: values?.status,
                    regex: values?.regex,
                    activity_state: activityState,
                    modified_year: modifiedYear,
                    recipient_emails: values?.recipient_emails,
                    adobe_io_integration_id: values?.adobe_io_integration_id
                };
                result = await updateTask(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    { id: saved_task_id, data }
                );
            } else {
                const data = {
                    feature_id: content.products.adobe_target.id,
                    saved_task_name: values.saved_task_name,
                    function_id: pathname.split('/').pop(),
                    adobe_io_integration_id: values?.adobe_io_integration_id,
                    time_interval: values.time_interval,
                    status: values.status,
                    regex: values?.regex,
                    activity_state: activityState,
                    modified_year: modifiedYear,
                    recipient_emails: values?.recipient_emails
                };
                result = await saveTask(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    data
                );
            }
            setShowSuccess(result.message);
            navigate("/functions/saved-tasks")
        } catch (error) {
            setShowError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    }

    // Transform Adobe IO integrations for dropdown
    const adobeIoOptions = userData?.adobeIoIntegrations?.map(integration => ({
        value: integration._id,
        name: integration.integration_name
    })) || [];

  return (
    <>
      <div className="w-full max-w-screen px-4 md:px-8 my-6">
        {loading && <Loader />}
        {showSuccess && (
          <SuccessAlert disappearTime={staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.timeout}>
            {staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.success}
          </SuccessAlert>
        )}
        <div className="flex flex-col lg:w-1/2 justify-center items-start">
          <div className="space-y-4 md:space-y-6 w-full">
            <Formik
                enableReinitialize
                initialValues={{
                    saved_task_name: data?.saved_task_name ? data?.saved_task_name : "",
                    time_interval: data?.time_interval || "",
                    status: data?.status || "",
                    email: "",
                    activity_state: data?.activity_state || "all",
                    modified_year: data?.modified_year || "all",
                    recipient_emails: data?.recipient_emails || [],
                    adobe_io_integration_id: data?.adobe_io_integration_id?._id || ""
                }}
                validationSchema={() => AdobeTargetActivitiesRevisionsSchema(staticContant.VALIDATE_NAME_CONTENT)}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, setFieldValue, values }) => (
                    <Form id="fill-validate-activity-name-form">
                        <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">
                            {staticContant.title}
                        </h2>
                        <p>{staticContant.description}</p>
                       <div className="flex flex-col md:flex-row gap-9 mb-4">
                            <div className="w-full" id="tour-saved_task_name">
                                <div className="relative">
                                    <Field
                                        type="text"
                                        id="saved_task_name"
                                        name="saved_task_name"
                                        className="block px-2.5 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                        placeholder=" "
                                        disabled={saved_task_id}
                                    />
                                    <Label labelFor="saved_task_name">{staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.saved_task_name}</Label>
                                    <Tooltip content={staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.saved_task_name} class_name="-right-6 top-1/2" />
                                </div>
                                {errors.saved_task_name && touched.saved_task_name && (
                                    <p className="mt-2 text-sm text-red-5">{errors.saved_task_name}</p>
                                )}
                            </div>

                            <div className="w-full" id="tour-adobe_io_integration_id">
                                <div className="relative">
                                    <Dropdown
                                        id="tour-adobe_io_integration_id-"
                                        width={100}
                                        isWidthInPercentage={true}
                                        initialValue={adobeIoOptions.find(x => x.value === values.adobe_io_integration_id)}
                                        items={adobeIoOptions}
                                        callback={(val) => setFieldValue("adobe_io_integration_id", val.value)}
                                    />
                                    <Label labelFor="adobe_io_integration_id">{staticContant.formLabels.adobe_io_integration_id}</Label>
                                    <Tooltip
                                        content={staticContant.help_text.adobe_io_integration_id}
                                        class_name="-right-6 top-1/2"
                                    />
                                </div>
                                {errors.adobe_io_integration_id && touched.adobe_io_integration_id && (
                                    <p className="mt-2 text-sm text-red-5">{errors.adobe_io_integration_id}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-8 mb-4">
                            <div className="w-full" id="tour-time_interval">
                                <div className="flex items-center gap-3">
                                    <div className="relative w-full">
                                        <Dropdown 
                                            id="tour-time_interval-"
                                            width={99}
                                            isWidthInPercentage={true}
                                            initialValue={content.CRON_TIME_INTERVAL.find(x => x.value == values.time_interval)}
                                            items={content.CRON_TIME_INTERVAL}
                                            callback={(val) => setFieldValue("time_interval", val.value)}
                                        />
                                        <Label labelFor="time_interval">{staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.time_interval}</Label>
                                        <Tooltip
                                            content={staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.time_interval}
                                            class_name="-right-6 top-1/2"
                                        />
                                    </div>
                                </div>
                                {errors.time_interval && touched.time_interval && (
                                    <p className="mt-2 text-sm text-red-5">{errors.time_interval}</p>
                                )}
                            </div>

                            <div className="w-full" id="tour-status">
                                <div className="flex items-center gap-3">
                                    <div className="relative w-full">
                                        <Dropdown 
                                            id="tour-status-"
                                            width={100}
                                            isWidthInPercentage={true}
                                            initialValue={content.LAMBDA_STATUS_VALUES.find(x => x.value == values.status)}
                                            items={content.LAMBDA_STATUS_VALUES}
                                            callback={(val) => setFieldValue("status", val.value)}
                                        />
                                        <Label labelFor="status">{staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.status}</Label>
                                        <Tooltip
                                            content={staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.status}
                                            class_name="-right-6 top-1/2"
                                        />
                                    </div>
                                </div>
                                {errors.status && touched.status && (
                                    <p className="mt-2 text-sm text-red-5">{errors.status}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-8 mb-4">
                            <div className="w-full" id="tour-activity_state">
                                <div className="flex items-center gap-3">
                                    <div className="relative w-full">
                                        <Dropdown 
                                            id="tour-activity_state-"
                                            width={100}
                                            isWidthInPercentage={true}
                                            initialValue={{ value: activityState, name: activityState === "all" ? "All" : activityState }}
                                            items={staticContant.activity_state_options}
                                            callback={(val) => setActivityState(val.value)}
                                        />
                                        <Label labelFor="activity_state">{staticContant.formLabels.activity_state}</Label>
                                        <Tooltip
                                            content={staticContant.help_text.activity_state}
                                            class_name="-right-6 top-1/2"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="w-full" id="tour-modified_year">
                                <div className="flex items-center gap-3">
                                    <div className="relative w-full">
                                        <Dropdown 
                                            id="tour-modified_year-"
                                            width={100}
                                            isWidthInPercentage={true}
                                            initialValue={{ value: modifiedYear, name: modifiedYear === "all" ? "All" : modifiedYear }}
                                            items={staticContant.modified_year_options}
                                            callback={(val) => setModifiedYear(val.value)}
                                        />
                                        <Label labelFor="modified_year">{staticContant.formLabels.modified_year}</Label>
                                        <Tooltip
                                            content={staticContant.help_text.modified_year}
                                            class_name="-right-6 top-1/2"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="my-4" id="tour-email_input">
                            <div className="relative">
                                <Field
                                    type="text"
                                    id="email"
                                    name="email"
                                    className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                    placeholder=" "
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if(!errors.email) {
                                                console.log(e.target.value)
                                                setFieldValue('recipient_emails', [...values.recipient_emails, e.target.value]);
                                                setFieldValue('email', '');
                                            }
                                        }
                                    }}
                                />
                                <Label labelFor="email">{staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.email}</Label>
                                <Tooltip content={staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.recipient_emails} class_name="-right-8 top-1/2" />
                            </div>
                            <p className="mt-2 text-sm ">{staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.note}</p>
                            {errors.email && touched.email ? (
                                <p className="mt-2 text-sm text-red-5">{errors.email}</p>
                            ) : null}
                            {!errors.email && errors.recipient_emails && touched.recipient_emails ? (
                            <   p className="mt-2 text-sm text-red-5">{errors.recipient_emails}</p>
                            ) : null}
                            {values.recipient_emails.length >0  && <ul className="max-h-[200px] max-w-[450px] overflow-y-auto my-4 border border-gray-2 rounded-lg ">
                                {values.recipient_emails.map((x, i) => (
                                    <li key={i} className="p-3 flex justify-between w-full border-b border-gray-2">
                                        {x}
                                        <button
                                            type="button"
                                            className="text-red-5"
                                            onClick={() => {
                                                const newRecipientEmails = [...values.recipient_emails];
                                                newRecipientEmails.splice(i, 1);
                                                setFieldValue('recipient_emails', newRecipientEmails);
                                            }}
                                        >x</button>
                                    </li>
                                ))}
                            </ul>}
                        </div>
                        {showError && <p className="my-2 text-sm text-red-5">{showError}</p>}
                        <div className="flex justify-end mt-6">
                            <PrimaryButton type="submit">
                                {staticContant.VALIDATE_ACTIVITY_NAME_CONTENT.submit_cta}
                            </PrimaryButton>
                        </div>

                    </Form>
                )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdobeTargetActivitiesRevisions;
